import React, { useState } from 'react';
import useForm from '../../hooks/useForm';
import FormField from '../common/FormField';
import { validateRequired } from '../../utils/validators';
import './ReferenceResponse.css';

const ReferenceResponse = ({ referenceToken }) => {
  const [submitted, setSubmitted] = useState(false);
  const [declined, setDeclined] = useState(false);

  // Mock data loaded via token
  const context = {
    applicantName: 'John Doe',
    type: 'EMPLOYMENT',
    respondentName: 'Jane Manager',
    relationship: 'Direct Manager'
  };

  const validate = (vals) => {
    const errs = {};
    errs.jobTitle = validateRequired(vals.jobTitle, 'Job Title');
    errs.confirmIdentity = vals.confirmIdentity ? null : 'You must confirm your identity.';
    errs.declaration = vals.declaration ? null : 'You must accept the declaration.';
    
    Object.keys(errs).forEach(k => errs[k] == null && delete errs[k]);
    return errs;
  };

  const { values, errors, handleChange, handleSubmit, setFieldValue } = useForm({
    confirmIdentity: false,
    jobTitle: '',
    stillEmployed: 'YES',
    salaryAccurate: 'YES',
    rehire: 'YES',
    comments: '',
    declaration: false
  }, validate);

  const onSubmit = (vals) => {
    setSubmitted(true);
  };

  const handleDecline = () => {
    setDeclined(true);
  };

  if (declined) {
    return (
      <div className="reference-response card">
        <div className="card-body text-center">
          <h2>Reference Declined</h2>
          <p>Thank you. We have notified the system that you are unable to provide a reference.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="reference-response card">
        <div className="card-body text-center">
          <h2 style={{color: 'var(--secondary)'}}>Reference Submitted Successfully</h2>
          <p>Thank you for taking the time to complete this reference for {context.applicantName}.</p>
          <p>You may now close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reference-response card">
      <div className="card-header bg-primary text-white">
        <h2>Reference Request: {context.applicantName}</h2>
      </div>
      <div className="card-body">
        <div className="reference-context">
          <p>You have been asked to provide an <strong>{context.type.toLowerCase()} reference</strong> for <strong>{context.applicantName}</strong> regarding a rental application.</p>
          <p>Your responses are strictly confidential and will be used solely for the purpose of assessing the applicant's suitability as a tenant.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>1. Identity Verification</h3>
            <label className="checkbox-label">
              <input type="checkbox" name="confirmIdentity" checked={values.confirmIdentity} onChange={handleChange} />
              <span>I confirm that I am <strong>{context.respondentName}</strong>, and I was/am the <strong>{context.relationship}</strong> of the applicant.</span>
            </label>
            {errors.confirmIdentity && <p className="error-text">{errors.confirmIdentity}</p>}
          </div>

          <div className="form-section">
            <h3>2. Questionnaire</h3>
            <FormField label="What was/is the applicant's job title?" name="jobTitle" value={values.jobTitle} onChange={handleChange} error={errors.jobTitle} required />
            
            <FormField label="Is the applicant still employed here?" name="stillEmployed" type="select" options={[{value:'YES', label:'Yes'}, {value:'NO', label:'No'}]} value={values.stillEmployed} onChange={handleChange} required />
            
            <FormField label="Would you rehire this person?" name="rehire" type="select" options={[{value:'YES', label:'Yes'}, {value:'NO', label:'No'}]} value={values.rehire} onChange={handleChange} required />
            
            <FormField label="Additional Comments" name="comments" type="textarea" value={values.comments} onChange={handleChange} helperText="Any additional information you think a prospective landlord should know." />
          </div>

          <div className="form-section">
            <h3>3. Declaration</h3>
            <label className="checkbox-label">
              <input type="checkbox" name="declaration" checked={values.declaration} onChange={handleChange} />
              <span>I declare that the information provided is true and accurate to the best of my knowledge.</span>
            </label>
            {errors.declaration && <p className="error-text">{errors.declaration}</p>}
          </div>

          <div className="reference-actions">
            <button type="button" className="btn-secondary" onClick={handleDecline}>I cannot provide a reference</button>
            <button type="submit" className="btn-primary">Submit Reference</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReferenceResponse;
